import { useState, useEffect } from "react";
import { generateClient } from "aws-amplify/api";
import { useIndustry } from "./industryContextHook";
import { captureError } from "./sentryHelpers";

const client = generateClient();

const listIndustryEventsQuery = /* GraphQL */ `
  query ListIndustryEvents($filter: ModelIndustryEventsFilterInput) {
    listIndustryEvents(filter: $filter) {
      items {
        event {
          id
          name {
            language
            text
          }
          status
          startDate
          endDate
          enableTicketBooking
        }
      }
    }
  }
`;

/**
 * Returns the next upcoming active event for the current industry.
 * @returns {{ currentEvent: object | null, loading: boolean }}
 */
export function useCurrentEvent() {
  const { currentIndustry } = useIndustry();
  const [currentEvent, setCurrentEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentIndustry?.id) {
      setLoading(false);
      return;
    }

    const fetchCurrentEvent = async () => {
      try {
        const { data } = await client.graphql({
          query: listIndustryEventsQuery,
          variables: { filter: { industryId: { eq: currentIndustry.id } } },
        });

        const events = (data?.listIndustryEvents?.items || [])
          .map((item) => item.event)
          .filter((event) => {
            if (event.status !== "active" || !event.endDate) return false;
            const endDate = new Date(event.endDate);
            endDate.setHours(23, 59, 59, 999);
            return endDate >= new Date();
          })
          .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

        setCurrentEvent(events[0] || null);
      } catch (error) {
        console.error("Error fetching current event:", error);
        captureError(error, {
          feature: "utils",
          operation: "fetch-current-event",
          context: { industryId: currentIndustry.id },
        });
        setCurrentEvent(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentEvent();
  }, [currentIndustry?.id]);

  return { currentEvent, loading };
}
